import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Download, Loader2, CheckCircle2, FileText, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Operator-facing page that displays the full AI-ad-agency handoff package
 * with a one-click "Copy All" button. The user kept getting frustrated
 * trying to copy/paste a server-side file — this lives inside the app so
 * the copy button just works.
 */
export default function AdPackage() {
    const [content, setContent] = useState("");
    const [stats, setStats] = useState({ bytes: 0, lines: 0 });
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        axios
            .get(`${API}/ad-package-public`)
            .then((r) => {
                setContent(r.data.content || "");
                setStats({ bytes: r.data.bytes || 0, lines: r.data.lines || 0 });
            })
            .catch(() => toast.error("Couldn't load the package. Refresh the page."))
            .finally(() => setLoading(false));
    }, []);

    const copyAll = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopied(true);
            toast.success("Copied — paste it straight into your ad agency app");
            setTimeout(() => setCopied(false), 3500);
        } catch {
            // Fallback for browsers without clipboard API
            const ta = document.createElement("textarea");
            ta.value = content;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
                setCopied(true);
                toast.success("Copied — paste it into your ad agency app");
                setTimeout(() => setCopied(false), 3500);
            } catch {
                toast.error("Couldn't copy. Try selecting the text manually.");
            }
            document.body.removeChild(ta);
        }
    };

    const downloadTxt = () => {
        const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "HPM_AD_PACKAGE_FULL.txt";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Downloaded HPM_AD_PACKAGE_FULL.txt");
    };

    return (
        <div className="min-h-screen bg-black text-white" data-testid="ad-package-page">
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                {/* HEADER + ACTIONS */}
                <div className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-6 sm:p-8 mb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2 flex items-center gap-2">
                        <FileText size={11} /> OPERATOR HANDOFF
                    </div>
                    <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-[0.95] mb-3">
                        AI Ad Agency Package
                    </h1>
                    <p className="font-sans text-white/75 leading-relaxed mb-6 max-w-3xl">
                        The complete brief to hand to any AI ad agency. Click <strong className="text-white">Copy All</strong>,
                        paste it into your agency&apos;s intake form. {stats.bytes > 0 && (
                            <span className="font-mono text-xs text-white/50">
                                ({stats.bytes.toLocaleString()} chars · {stats.lines.toLocaleString()} lines)
                            </span>
                        )}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={copyAll}
                            disabled={loading || !content}
                            className="btn-brutal btn-brutal-red text-[10px]"
                            data-testid="ad-package-copy-btn"
                        >
                            {copied ? (
                                <>
                                    <CheckCircle2 size={14} /> COPIED — PASTE NOW
                                </>
                            ) : (
                                <>
                                    <Copy size={14} /> COPY ALL ({stats.bytes.toLocaleString()} CHARS)
                                </>
                            )}
                        </button>
                        <button
                            onClick={downloadTxt}
                            disabled={loading || !content}
                            className="btn-brutal text-[10px]"
                            data-testid="ad-package-download-btn"
                        >
                            <Download size={14} /> DOWNLOAD AS .TXT
                        </button>
                        <a
                            href={`${API}/ad-package-public`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 border-2 border-white/20 hover:border-white/60 transition-colors px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white"
                            data-testid="ad-package-raw-btn"
                        >
                            <ExternalLink size={12} /> VIEW RAW JSON
                        </a>
                    </div>
                </div>

                {/* PREVIEW */}
                <div className="border-2 border-white/15 bg-black/50">
                    <div className="flex items-center justify-between border-b border-white/15 px-5 py-3">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                            FULL PACKAGE — PREVIEW
                        </div>
                        <button
                            onClick={copyAll}
                            disabled={loading || !content}
                            className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] hover:text-white transition-colors flex items-center gap-2"
                            data-testid="ad-package-copy-mini-btn"
                        >
                            {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                            {copied ? "COPIED" : "COPY"}
                        </button>
                    </div>
                    <div className="p-5">
                        {loading ? (
                            <div className="flex items-center gap-3 text-white/50 font-mono text-xs py-12 justify-center">
                                <Loader2 size={16} className="animate-spin" /> LOADING PACKAGE...
                            </div>
                        ) : (
                            <pre
                                className="font-mono text-[11px] leading-relaxed text-white/85 whitespace-pre-wrap max-h-[600px] overflow-y-auto"
                                data-testid="ad-package-preview"
                            >
                                {content}
                            </pre>
                        )}
                    </div>
                </div>

                {/* HOW TO USE */}
                <div className="mt-8 border-2 border-white/15 p-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF85] mb-3">
                        HOW TO USE THIS
                    </div>
                    <ol className="font-sans text-white/80 leading-relaxed space-y-3 list-decimal pl-6">
                        <li>Click <strong className="text-white">COPY ALL</strong> above.</li>
                        <li>Open your AI ad agency app (the one you built on emergent.sh).</li>
                        <li>Paste into its intake / brief / chat field.</li>
                        <li>Ask the agency to quote a flat monthly retainer against the deliverables in Section 6 (Budget &amp; Pacing).</li>
                        <li>If the agency pushes back on the &quot;$0 paid ads until 5 presales&quot; rule, walk away — they&apos;re not a fit.</li>
                    </ol>
                </div>
            </main>
            <Footer disclaimer="Operator handoff document. Do not share publicly without redacting strategy specifics." />
        </div>
    );
}
